import {
  type Home,
  type HomeDto,
  type Resident,
  ResidentStatus
} from '@lmc/ts-common'
import { Avatar } from '@lmc/ui-kit'
import { getNominative } from 'common/utils'
import { type FC, useEffect } from 'react'
import { useSelector } from 'react-redux'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import { getFundingSourceName } from 'screens/Rostering/utils'
import { ServiceUserDetails } from '../../formComponents'
import { getNewAddress, type ModalSectionProps } from '../utils'

type ServiceUserDetailsSectionProps = ModalSectionProps & {
  disableResident?: boolean
  disableAddress?: boolean
}

export const ServiceUserDetailsSection: FC<ServiceUserDetailsSectionProps> = ({
  formData,
  form,
  disableResident,
  disableAddress
}) => {
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const { residents, refetchResidents } = useResidentsList({})

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  useEffect(() => {
    refetchResidents()
  }, [])

  const groupedResidents: { [status: string]: Resident[] } = {
    [ResidentStatus.active]: [],
    [ResidentStatus.inactive]: [],
    [ResidentStatus.hospitalised]: [],
    [ResidentStatus.deceased]: [],
    [ResidentStatus.discharged]: []
  }

  residents?.forEach((resident: Resident) => {
    const { status } = resident
    if (!groupedResidents[status]) {
      groupedResidents[status] = []
    }
    groupedResidents[status].push(resident)
  })

  Object.keys(groupedResidents).forEach((key) => {
    if (groupedResidents[key].length === 0) {
      delete groupedResidents[key]
    } else {
      groupedResidents[key].sort((a, b) => {
        const nameA = `${a.name.first} ${a.name.last}`
        const nameB = `${b.name.first} ${b.name.last}`
        return nameA.localeCompare(nameB)
      })
    }
  })

  const groupedResidentOptions =
    Object.keys(groupedResidents).map((status) => ({
      label: status.toLocaleUpperCase(),
      options: groupedResidents[status].map((resident) => ({
        label: `${resident.name.first} ${resident.name.last}`,
        value: resident.id,
        image: <Avatar url={resident.picture as unknown as string} size={20} />
      }))
    })) || []

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const selectedResident = formData?.subjectId

  const foundResident = residents?.find(
    (resident) => resident?.id === selectedResident
  )

  const hasValidFundingSources =
    foundResident?.carePackage?.fundingSources instanceof Array

  const fundingTypeOptions = hasValidFundingSources
    ? (foundResident?.carePackage?.fundingSources?.map((source) => ({
        value: source.type,
        label: getFundingSourceName(source, home)
      })) ?? [])
    : []

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  useEffect(() => {
    if (form?.isFieldsTouched()) {
      form?.setFieldValue('fundingTypeId', undefined)
      form?.setFieldValue('addressId', undefined)
    }
  }, [selectedResident, form])

  // Auto-select runs separately so it fires when async options arrive, not just on
  // resident change. Uses the first option's value as a dep to stay stable once loaded.
  const singleOptionValue =
    fundingTypeOptions.length === 1 ? fundingTypeOptions[0].value : undefined
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  useEffect(() => {
    if (singleOptionValue) {
      form?.setFieldValue('fundingTypeId', singleOptionValue)
    }
  }, [selectedResident, singleOptionValue, form])

  const addressOptions = hasValidFundingSources
    ? foundResident?.addresses?.map((address) => ({
        // @ts-ignore
        // eslint-disable-next-line no-underscore-dangle
        value: `${address?._id || address?.suburb || address?.name}`,
        label: `${getNewAddress(address, true)}`
      }))
    : []

  return (
    <ServiceUserDetails
      preferredPeopleNominative={getNominative(
        preferredPeopleNominative,
        'Resident'
      )}
      residentOptions={groupedResidentOptions}
      fundingTypeOptions={fundingTypeOptions}
      addressOptions={addressOptions}
      disableResident={disableResident}
      disableAddress={disableAddress}
      formData={formData}
    />
  )
}
